import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { IonicModule, NavParams } from "@ionic/angular";
import { ModalController, ToastController } from "@ionic/angular/standalone";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import intlTelInput from "intl-tel-input";
import { NgxMaskDirective, provideNgxMask } from "ngx-mask";
import { ProverHorasComponent } from "src/app/components/prover-horas/prover-horas.component";
import { SemRegistroComponent } from "src/app/components/sem-registro/sem-registro";
import { PerfilProfissional } from "src/app/interfaces/perfil-profissional";
import { DateFormatPipe } from "src/app/pipes/date-format/date-format";
import { noWhitespaceValidator } from "src/app/pipes/no-white-space-validator/no-white-space-validator";
import { InstituicaoProvider } from "src/app/services/instituicao/instituicao";
import { profissionalProvider } from "src/app/services/profissional/profissional";

@Component({
  selector: "app-perfil-profissional",
  templateUrl: "./perfil-profissional.page.html",
  styleUrls: ["./perfil-profissional.page.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    ReactiveFormsModule,
    TranslateModule,
    DateFormatPipe,
    NgxMaskDirective,
    ProverHorasComponent,
    SemRegistroComponent
  ],
  providers: [provideNgxMask()],
})
export class PerfilProfissionalPage implements OnInit {
  @ViewChild("phoneInput", { read: ElementRef }) phoneInput: ElementRef;

  submitted: boolean;
  validations_form: FormGroup;
  idPessoa: string;
  pessoaNome: string;
  idProfissao: string;
  dadosPessoa: any;
  lastIso2: any;
  lastIsoMaster: string;
  maskTel: string;
  ddiTelComercial = "+55";
  infoEmpresa: any;
  validation_messages = {
    nome_empresa: [{ type: "required", message: this.translate.instant("Informe o nome da empresa") }],
    descricao: [{ type: "required", message: this.translate.instant("Digite uma descrição válida") }],
    telComercial: [{ type: "required", message: this.translate.instant("Digite um número válido") }]
  };

  constructor(
    public modalCtrl: ModalController,
    public instituicaoProvider: InstituicaoProvider,
    private cdr: ChangeDetectorRef,
    public formBuilder: FormBuilder,
    private profissionalProvider: profissionalProvider,
    public navParams: NavParams,
    public toastCtrl: ToastController,
    public translate: TranslateService,
  ) {
    this.validations_form = this.formBuilder.group({
      nome_empresa: new FormControl(null, [Validators.required, noWhitespaceValidator]),
      descricao: new FormControl(null, [Validators.required, noWhitespaceValidator]),
      telComercial: new FormControl(null, Validators.required),
      email: new FormControl(null, Validators.nullValidator),
      site: new FormControl(null, Validators.nullValidator),
      linkedin: new FormControl(null, Validators.nullValidator),
      instagram: new FormControl(null, Validators.nullValidator),
      facebook: new FormControl(null, Validators.nullValidator),
      whatsappComercial: new FormControl(null, Validators.nullValidator),
    });
    this.idPessoa = this.navParams.get("idPessoa");
    this.pessoaNome = this.navParams.get("pessoaNome");
    this.idProfissao = this.navParams.get("idProfissao");
    this.dadosEmpresarial();
  }

  ngOnInit() {}

  dadosEmpresarial() {
    this.profissionalProvider.buscarEmpresa(this.idPessoa).subscribe((data) => {
      this.infoEmpresa = data.empresa;
      this.dadosPessoa = data.pessoa;
      this.ddiTelComercial = this.dadosPessoa.ddiTelComercial;
      this.validations_form.patchValue({
        telComercial: this.dadosPessoa.telComercial || null,
        whatsappComercial: this.dadosPessoa.whatsappComercial == "1" ? true : false,
      });

      if (this.infoEmpresa) {
        this.validations_form.patchValue({
          nome_empresa: this.infoEmpresa.nome_empresa,
          descricao: this.infoEmpresa.descricao,
          email: this.infoEmpresa.email,
          site: this.infoEmpresa.links.site,
          linkedin: this.infoEmpresa.links.linkedin,
          instagram: this.infoEmpresa.links.instagram,
          facebook: this.infoEmpresa.links.facebook,
        });
      }

      setTimeout(() => {
        this.inputMask();
      });
    });
  }

  onSubmit() {
    this.submitted = true;

    if (this.validations_form.invalid) {
      this.validations_form.markAllAsTouched();
      return;
    }

    const formValues = this.validations_form.getRawValue();
    const links = {
      site: formValues.site || null,
      linkedin: formValues.linkedin || null,
      instagram: formValues.instagram || null,
      facebook: formValues.facebook || null,
    };

    const payload: PerfilProfissional = {
      ...formValues,
      links: links,
      ddiTelComercial: this.ddiTelComercial,
      whatsappComercial: formValues.whatsappComercial ? 1 : 0,
      idPessoa: this.idPessoa,
      id: this.infoEmpresa?.id || null,
    };

    this.profissionalProvider.salvarPerfilProfissional(payload).subscribe(async (data: PerfilProfissional) => {
      const toast = await this.toastCtrl.create({
        message: this.translate.instant("Salvo com sucesso!"),
        duration: 3000,
      });
      toast.present();
      this.modalCtrl.dismiss();
    });
  }

  closeModal() {
    this.modalCtrl.dismiss();
  }

  inputMask() {
    const inputElement = this.phoneInput.nativeElement;
    this.lastIso2 = this.instituicaoProvider.objDddi()[this.ddiTelComercial];
    this.lastIsoMaster = this.lastIso2;
    this.applyPhoneMask();

    const iti = intlTelInput(inputElement, {
      countryOrder: ["Us", "Gb", "Br", "Es", "Sa", "Ca"],
      separateDialCode: true,
      initialCountry: this.lastIso2,
      formatOnDisplay: true,
      formatAsYouType: true,
      countrySearch: false,
    });

    inputElement.addEventListener("countrychange", () => {
      const selectedCountryData = iti.getSelectedCountryData();

      if (selectedCountryData) {
        this.lastIso2 = selectedCountryData.iso2 || "";
        this.lastIsoMaster = selectedCountryData.iso2 || "";
        this.ddiTelComercial = "+" + selectedCountryData.dialCode;
        this.applyPhoneMask();
      } else {
        this.applyPhoneMask();
      }
    });

    inputElement.addEventListener("input", (event: any) => {
      if (event.inputType === "deleteContentBackward") {
        this.lastIso2 = this.lastIsoMaster;
      }
      this.applyPhoneMask();
    });
  }

  async applyPhoneMask() {
    const objectMask: { [key: string]: string } = {
      ac: "0000",
      ad: "000-000",
      ae: "00-000-0000",
      af: "00-000-0000",
      ag: "(000)000-0000",
      ai: "(000)000-0000",
      al: "(000)000-000",
      am: "00-000-000",
      an: "0000-0000",
      ao: "(000)000-000",
      aq: "000-000",
      ar: "(000)000-0000",
      as: "(000)000-0000",
      at: "(000)000-0000",
      au: "0-0000-0000",
      aw: "000-0000",
      az: "00-000-00-00",
      ba: "00-00000",
      bb: "(000)000-0000",
      bd: "00-000-000",
      be: "(000)000-000",
      bf: "00-00-0000",
      bg: "(000)000-000",
      bh: "0000-0000",
      bi: "00-00-0000",
      bj: "00-00-0000",
      bm: "(000)000-0000",
      bn: "000-0000",
      bo: "0-000-0000",
      br: "(00)00000-0000",
      bs: "(000)000-0000",
      bt: "00-000-000",
      bw: "00-000-000",
      by: "(00)000-00-00",
      bz: "000-0000",
      ca: "(000)000-0000",
      cd: "(000)000-000",
      cf: "00-00-0000",
      cg: "00-000-0000",
      ch: "00-000-0000",
      ci: "00-000-000",
      ck: "00-000",
      cl: "0-0000-0000",
      cm: "0000-0000",
      cn: "(000)0000-0000",
      co: "(000)000-0000",
      cr: "0000-0000",
      cu: "0-000-0000",
      cv: "(000)00-00",
      cw: "000-0000",
      cy: "00-000-000",
      cz: "(000)000-000",
      de: "(0000)000-0000",
      dj: "00-00-00-00-00",
      dk: "00-00-00-00-00",
      dm: "(000)000-0000",
      do: "(000)000-0000",
      dz: "00-000-0000",
      ec: "00-000-0000",
      ee: "0000-0000",
      eg: "(000)000-0000",
      er: "0-000-000",
      es: "(000)000-000",
      et: "00-000-0000",
      fi: "(000)000-00-00",
      fj: "00-00000",
      fk: "00000",
      fm: "000-0000",
      fo: "000-000",
      fr: "(000)000-000",
      ga: "0-00-00-00-00",
      gd: "(000)000-0000",
      ge: "(000)000-000",
      gf: "00000-0000",
      gh: "(000)000-000",
      gi: "000-00000",
      gl: "00-00-00-00",
      gm: "(000)00-00",
      gn: "00-000-000",
      gq: "00-000-0000",
      gr: "(000)000-0000",
      gt: "0-000-0000",
      gu: "(000)000-0000",
      gw: "0-000000",
      gy: "000-0000",
      hk: "0000-0000",
      hn: "0000-0000",
      hr: "00-000-000",
      ht: "00-00-0000",
      hu: "(000)000-000",
      id: "(000)000-00-000",
      ie: "(000)000-000",
      il: "00-000-0000",
      in: "(0000)000-000",
      io: "000-0000",
      iq: "(000)000-0000",
      ir: "(000)000-0000",
      is: "000-0000",
      it: "(000)0000-000",
      jm: "(000)000-0000",
      jo: "0-0000-0000",
      jp: "(000)000-000",
      ke: "000-000-0000",
      kg: "(000)000-000",
      kh: "00-000-000",
      ki: "00-000",
      km: "00-00000",
      kn: "(000)000-0000",
      kp: "000-0000-000",
      kr: "00-000-0000",
      kw: "0000-0000",
      ky: "(000)000-0000",
      kz: "(000)000-00-00",
      la: "(0000)000-000",
      lb: "00-000-000",
      lc: "(000)000-0000",
      li: "(000)000-0000",
      lk: "00-000-0000",
      lr: "00-000-000",
      ls: "0-000-0000",
      lt: "(000)00-000",
      lu: "(000)000-000",
      lv: "00-000-000",
      ly: "00-000-000",
      ma: "00-0000-000",
      mc: "(000)000-000",
      md: "0000-0000",
      me: "00-000-000",
      mg: "00-00-00000",
      mh: "000-0000",
      mk: "00-000-000",
      ml: "00-00-0000",
      mm: "00-000-000",
      mn: "00-00-0000",
      mo: "0000-0000",
      mp: "(000)000-0000",
      mq: "(000)00-00-00",
      mr: "00-00-0000",
      ms: "(000)000-0000",
      mt: "0000-0000",
      mu: "000-0000",
      mv: "000-0000",
      mw: "0-0000-0000",
      mx: "(000)000-0000",
      my: "(000)000-000",
      mz: "00-000-000",
      na: "00-000-0000",
      nc: "00-0000",
      ne: "00-00-0000",
      nf: "000-000",
      ng: "(000)000-0000",
      ni: "0000-0000",
      nl: "00-000-0000",
      no: "(000)00-000",
      np: "00-000-000",
      nr: "000-0000",
      nu: "0000",
      nz: "(000)000-000",
      om: "00-000-000",
      pa: "000-0000",
      pe: "(000)000-000",
      pf: "00-00-00",
      pg: "(000)00-000",
      ph: "(000)000-0000",
      pk: "(000)000-0000",
      pl: "(000)000-000",
      ps: "00-000-0000",
      pt: "00-000-0000",
      pw: "000-0000",
      py: "(000)000-000",
      qa: "0000-0000",
      re: "00000-0000",
      ro: "00-000-0000",
      rs: "00-000-0000",
      ru: "(000)000-00-00",
      rw: "(000)000-000",
      sa: "(0000) 000 000",
      sb: "000-0000",
      sc: "0-000-000",
      sd: "00-000-0000",
      se: "00-000-0000",
      sg: "0000-0000",
      sh: "0000",
      si: "00-000-000",
      sk: "(000)000-000",
      sl: "00-000000",
      sm: "0000-000000",
      sn: "00-000-0000",
      so: "00-000-000",
      sr: "000-0000",
      ss: "00-000-0000",
      st: "00-00000",
      sv: "00-00-0000",
      sx: "(000)000-0000",
      sy: "00-0000-000",
      sz: "00-00-0000",
      tc: "(000)000-0000",
      td: "00-00-00-00",
      tg: "00-000-000",
      th: "00-000-0000",
      tj: "00-000-0000",
      tk: "0000",
      tl: "000-0000",
      tm: "0-000-0000",
      tn: "00-000-000",
      to: "00000",
      tr: "(000)000-0000",
      tt: "(000)000-0000",
      tv: "000000",
      tw: "0-0000-0000",
      tz: "00-000-0000",
      ua: "(00)000-00-00",
      ug: "(000)000-000",
      gb: "00-0000-000000",
      us: "(000)000-0000",
      uy: "0-000-00-00",
      uz: "00-000-0000",
      va: "0-000-00000",
      vc: "(000)000-0000",
      ve: "(000)000-0000",
      vg: "(000)000-0000",
      vi: "(000)000-0000",
      vn: "(000)0000-000",
      vu: "00-00000",
      wf: "00-0000",
      ws: "00-0000",
      ye: "000-000-000",
      za: "00-000-0000",
      zm: "00-000-0000",
      zw: "0-000000",
    };

    const numberMask = objectMask[this.lastIso2].replace(/\D/g, "");
    const maskObj = {
      pais: this.lastIso2,
      mask: objectMask[this.lastIso2],
      maxDigits: numberMask.length,
    };

    this.maskTel = maskObj.mask;
    this.cdr.detectChanges();
  }
}
